import { AxiosError } from 'axios'
import { DocumentCreate, DocumentUpdate } from '../models/request'
import { ErrorResult } from '../../../../shared/models/types'
import { collectDeletableDocumentFiles } from '../models/selectors'
import { deleteDocument } from '../api/deleteDocument'
import { deleteFile } from '../api/deleteFile'

function isDeletedPersistedDocument(document: DocumentUpdate) {
  return document.deleted
}

async function deleteDocuments(
  documents: DocumentUpdate[],
) {
  const deletableDocuments = documents.filter(isDeletedPersistedDocument)
  if (!deletableDocuments.length) return { deletedDocuments: [], errors: [] }

  const deletePromises = await Promise.allSettled(deletableDocuments
    .map((d) => deleteDocument(d.id as number)))

  const errors: ErrorResult[] = []

  const deletedDocuments = []
  for (let i = 0; i < deletePromises.length; i++) {
    const result = deletePromises[i]
    if (result.status === 'fulfilled') {
      deletedDocuments.push(deletableDocuments[i])
    } else {
      const err = result.reason as AxiosError
      errors.push({
        message: err.message,
      })
    }
  }

  return {
    deletedDocuments,
    errors,
  }
}

async function deleteDocumentFiles(
  documents: DocumentCreate[],
) {
  const requests = collectDeletableDocumentFiles(documents).flatMap((f) => {
    if (!f.uuid) return []
    const [folder, fileUuid] = f.uuid.split('/')
    return [deleteFile({ folder, fileUuid })]
  })

  if (!requests.length) return { errors: [] }

  const results = await Promise.allSettled(requests)
  const hasRejected = results.some((result) => result.status === 'rejected')

  return {
    errors: hasRejected
      ? [{ message: 'Failed to delete some files' }]
      : [],
  }
}

function createDocumentDeleter() {
  return async function deleteDeletedDocuments(documents: DocumentUpdate[]) {
    const {
      deletedDocuments,
      errors: deleteDocumentErrors,
    } = await deleteDocuments(documents)

    const {
      errors: deleteFileErrors,
    } = await deleteDocumentFiles(deletedDocuments)

    return {
      deletedDocuments,
      errors: [...deleteDocumentErrors, ...deleteFileErrors],
    }
  }
}

export default createDocumentDeleter
